/**
 * Which of your agents can be run from Protect, and what the platform's answer
 * to each message means.
 *
 * <h3>Eligibility is read, never recomputed</h3>
 *
 * The rule is stated once, in the model: `Agent.servesGovernedTraffic()` — a
 * native agent when it is published, an ExteLet once it is execution ready. The
 * agent row carries that boolean, and this module reads it.
 *
 * It deliberately does not fall back to `status` or `executionReadyAt` when the
 * boolean is missing. A fallback would be a second place that decides whether
 * an agent may be run, and the day the two disagree, Protect offers a run the
 * platform refuses — or, worse, runs something ungoverned. A row without the
 * field is a row where the platform did not say, and not saying is not a yes.
 *
 * <h3>A verdict is reported, never inferred</h3>
 *
 * `useTestRun` derives allowed / enforced / blocked from the response; this maps
 * that to what the transcript paints. An answer with no policy result gets no
 * verdict at all, because a tick on a call policy never saw is the one thing
 * this surface must not show.
 */

/** What the transcript paints for each action the platform reported. */
const PAINTED = { ALLOWED: 'ALLOW', ENFORCED: 'TRANSFORM', BLOCKED: 'DENY' };

export function eligibleForRun(agent) {
  return agent?.servesGovernedTraffic === true;
}

export function runnableAgents(rows) {
  return (Array.isArray(rows) ? rows : []).filter(eligibleForRun);
}

export function toTurns(messages) {
  return (Array.isArray(messages) ? messages : []).map((m, i) => {
    const text = String(m?.content ?? '');
    if (m?.role === 'user') return { id: `t${i}`, who: 'you', text };

    /* A thrown error is not an answer. It is shown on the agent's side because
       that is where the reply would have been, and marked so it cannot be read
       as one. */
    if (m?.isError) return { id: `t${i}`, who: 'agent', text, error: true };

    const tools = Array.isArray(m?.toolResults) ? m.toolResults.length : 0;
    return {
      id: `t${i}`,
      who: 'agent',
      text,
      verdict: PAINTED[m?.policyAction] || undefined,
      notes: tools ? [`${tools} tool call${tools === 1 ? '' : 's'}`] : [],
    };
  });
}
